import { useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { App } from "@capacitor/app";
import { supabase } from "@/integrations/supabase/external-client";

export const APP_KEY = "vendor" as const;

const VERSION_STORAGE_KEY = "_vendor_app_last_version";

export interface AppVersionConfig {
  app_key: string;
  package_name: string;
  min_version: string;
  latest_version: string;
  play_store_url: string;
  update_title: string | null;
  update_message: string | null;
}

export type GateStatus = "checking" | "ok" | "update_required";

export interface AppVersionGateState {
  status: GateStatus;
  currentVersion: string | null;
  config: AppVersionConfig | null;
}

// "1.2.10" -> [1, 2, 10]. Non-numeric segments treated as 0.
function parseVersion(v: string): number[] {
  return (v || "0").split(/[.\-+]/).map((p) => {
    const n = parseInt(p.replace(/[^\d]/g, ""), 10);
    return Number.isFinite(n) ? n : 0;
  });
}

// returns negative if a<b, 0 if equal, positive if a>b
function compareVersions(a: string, b: string): number {
  const aa = parseVersion(a);
  const bb = parseVersion(b);
  const len = Math.max(aa.length, bb.length);
  for (let i = 0; i < len; i++) {
    const diff = (aa[i] ?? 0) - (bb[i] ?? 0);
    if (diff !== 0) return diff;
  }
  return 0;
}

let cachedOk = false;

export function useAppVersionGate(): AppVersionGateState {
  const [state, setState] = useState<AppVersionGateState>(() => ({
    status: cachedOk || !Capacitor.isNativePlatform() ? "ok" : "checking",
    currentVersion: null,
    config: null,
  }));

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    if (cachedOk) return;

    let cancelled = false;

    (async () => {
      try {
        const [info, configRes] = await Promise.all([
          App.getInfo(),
          supabase
            .from("app_version_config" as any)
            .select(
              "app_key, package_name, min_version, latest_version, play_store_url, update_title, update_message"
            )
            .eq("app_key", APP_KEY)
            .maybeSingle(),
        ]);

        if (cancelled) return;

        const currentVersion = info?.version ?? null;

        // Detect Play Store update: localStorage survives APK updates on Android
        // so stale auth tokens and cached state from the previous version cause
        // broken behaviour. Clearing auth and reloading once ensures the new
        // bundle starts clean. Reinstalling already does this implicitly.
        if (currentVersion) {
          const storedVersion = localStorage.getItem(VERSION_STORAGE_KEY);
          if (storedVersion && storedVersion !== currentVersion) {
            localStorage.setItem(VERSION_STORAGE_KEY, currentVersion);
            try {
              await supabase.auth.signOut({ scope: "local" });
            } catch {}
            window.location.reload();
            return;
          }
          if (!storedVersion) {
            localStorage.setItem(VERSION_STORAGE_KEY, currentVersion);
          }
        }

        const config = (configRes.data as unknown as AppVersionConfig | null) ?? null;

        if (!config || !currentVersion) {
          // Fail open on missing config — never lock users out due to a
          // backend hiccup.
          cachedOk = true;
          setState({ status: "ok", currentVersion, config });
          return;
        }

        if (compareVersions(currentVersion, config.min_version) < 0) {
          setState({ status: "update_required", currentVersion, config });
        } else {
          cachedOk = true;
          setState({ status: "ok", currentVersion, config });
        }
      } catch {
        if (cancelled) return;
        // Fail open on network/RPC errors.
        cachedOk = true;
        setState((s) => ({ ...s, status: "ok" }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}